import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Printer, Pill } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

function todayStr() { return new Date().toISOString().slice(0, 10); }

const COLORS = { primary: [27, 67, 50], muted: [110, 115, 110], text: [26, 28, 26] };

function generateRecetaPdf(receta, patient) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;

  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, W, 90, "F");
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, 28, 34, 34, 3, 3, "F");
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("DC", margin + 11, 50);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20); doc.text("DentCloud", margin + 50, 47);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text("Receta médica odontológica", margin + 50, 64);
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text("RECETA", W - margin, 47, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(receta.fecha, W - margin, 64, { align: "right" });

  let y = 120;
  doc.setTextColor(...COLORS.text); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Paciente", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(`${patient.name}`, margin, y); y += 12;
  if (patient.document_number) { doc.text(`Doc: ${patient.document_type || ""} ${patient.document_number}`, margin, y); y += 12; }
  if (receta.diagnostico) { doc.text(`Diagnóstico: ${receta.diagnostico}`, margin, y); y += 12; }
  y += 6;

  autoTable(doc, {
    startY: y,
    head: [["Medicamento", "Presentación", "Dosis", "Duración", "Indicaciones"]],
    body: receta.items.map((i) => [i.medicamento, i.presentacion || "", i.dosis || "", i.duracion || "", i.indicaciones || ""]),
    theme: "grid",
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: COLORS.text },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 16;

  if (receta.indicaciones_generales) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("Indicaciones generales", margin, y); y += 12;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const wrap = doc.splitTextToSize(receta.indicaciones_generales, W - 2 * margin);
    doc.text(wrap, margin, y); y += wrap.length * 11 + 10;
  }

  // Firma
  y += 40;
  doc.setDrawColor(180, 180, 180);
  doc.line(W - margin - 220, y, W - margin, y);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...COLORS.text);
  doc.text("Firma del odontólogo", W - margin - 110, y + 14, { align: "center" });
  if (receta.doctor?.name) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...COLORS.muted);
    doc.text(receta.doctor.name, W - margin - 110, y + 26, { align: "center" });
    let oy = 38;
    if (receta.doctor.specialty) { doc.text(receta.doctor.specialty, W - margin - 110, y + oy, { align: "center" }); oy += 11; }
    if (receta.doctor.registry_number) { doc.text(receta.doctor.registry_number, W - margin - 110, y + oy, { align: "center" }); oy += 11; }
    if (receta.doctor.signature_text) {
      const wrap = doc.splitTextToSize(receta.doctor.signature_text, 220);
      doc.text(wrap, W - margin - 110, y + oy, { align: "center" });
    }
  }

  doc.save(`receta_${patient.name.replace(/\s+/g, "_")}_${receta.fecha}.pdf`);
}

export default function RecetasList({ patientId, patient }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    fecha: todayStr(), diagnostico: "", indicaciones_generales: "",
    items: [{ medicamento: "", presentacion: "", dosis: "", duracion: "", indicaciones: "" }],
  });

  const load = async () => {
    try { const { data } = await api.get(`/patients/${patientId}/recetas`); setItems(data); }
    catch {}
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line

  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, { medicamento: "", presentacion: "", dosis: "", duracion: "", indicaciones: "" }] }));
  const setRow = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const delRow = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter((i) => i.medicamento.trim());
    if (items.length === 0) { toast.error("Agrega al menos un medicamento"); return; }
    try {
      await api.post(`/patients/${patientId}/recetas`, { ...form, items });
      toast.success("Receta guardada");
      setForm({ fecha: todayStr(), diagnostico: "", indicaciones_generales: "", items: [{ medicamento: "", presentacion: "", dosis: "", duracion: "", indicaciones: "" }] });
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar receta?")) return;
    try { await api.delete(`/recetas/${id}`); load(); } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="space-y-4" data-testid="recetas-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Recetas</h2>
          <p className="text-sm text-muted-foreground">Genera prescripciones imprimibles en PDF.</p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-primary" data-testid="new-receta-btn">
          <Plus className="w-4 h-4" /> {open ? "Cancelar" : "Nueva receta"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="card-flat p-5 space-y-3" data-testid="receta-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Fecha *</label>
              <input type="date" required className="input-flat font-mono" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Diagnóstico</label>
              <input className="input-flat" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="label-eyebrow">Medicamentos</div>
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <input className="col-span-3 input-flat" placeholder="Medicamento *" value={it.medicamento} onChange={(e) => setRow(i, "medicamento", e.target.value)} required />
                <input className="col-span-2 input-flat" placeholder="Presentación" value={it.presentacion} onChange={(e) => setRow(i, "presentacion", e.target.value)} />
                <input className="col-span-2 input-flat" placeholder="Dosis" value={it.dosis} onChange={(e) => setRow(i, "dosis", e.target.value)} />
                <input className="col-span-2 input-flat" placeholder="Duración" value={it.duracion} onChange={(e) => setRow(i, "duracion", e.target.value)} />
                <input className="col-span-2 input-flat" placeholder="Indicaciones" value={it.indicaciones} onChange={(e) => setRow(i, "indicaciones", e.target.value)} />
                <button type="button" onClick={() => delRow(i)} className="col-span-1 p-2 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addRow} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Agregar medicamento
            </button>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Indicaciones generales</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.indicaciones_generales} onChange={(e) => setForm({ ...form, indicaciones_generales: e.target.value })} />
          </div>
          <button type="submit" className="btn-primary" data-testid="receta-save">Guardar receta</button>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="card-flat p-12 text-center text-sm text-muted-foreground">Sin recetas registradas.</div>
        ) : items.map((it) => (
          <div key={it.id} className="card-flat p-4" data-testid={`receta-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Pill className="w-4 h-4 text-primary" />
                  <span className="font-mono text-xs text-muted-foreground">{it.fecha}</span>
                  {it.doctor?.name && <span className="text-xs text-muted-foreground">por {it.doctor.name}</span>}
                </div>
                {it.diagnostico && <div className="text-sm mb-2"><b>Dx:</b> {it.diagnostico}</div>}
                <div className="space-y-1">
                  {(it.items || []).map((m, idx) => (
                    <div key={idx} className="text-sm">
                      <b>{m.medicamento}</b>
                      {m.presentacion && <span className="text-muted-foreground"> · {m.presentacion}</span>}
                      {m.dosis && <span className="text-muted-foreground"> · {m.dosis}</span>}
                      {m.duracion && <span className="text-muted-foreground"> · {m.duracion}</span>}
                    </div>
                  ))}
                </div>
                {it.indicaciones_generales && (
                  <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">{it.indicaciones_generales}</div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => generateRecetaPdf(it, patient)} className="p-2 text-primary hover:bg-primary/10 rounded-sm" title="Imprimir PDF" data-testid={`receta-pdf-${it.id}`}>
                  <Printer className="w-4 h-4" />
                </button>
                <button onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
